import React, {createContext} from 'react';
import _ from 'lodash';
import {AstView, EnrichedAstNode, findNodeById} from 'rendition-core';
import {RepairMode, repair} from 'rendition-core/layout/RepairGroup';

import {syncViewToSimulatedV2} from 'rendition-core/layout/Simulate/ChangeHelpers';

export type OrMixed<T> = {
    [P in keyof T]: T[P] extends object ? OrMixed<T[P]> : T[P] | 'Mixed';
};

interface ViewContextState {
    setView: (view: AstView, options?: Partial<SetViewOptions>) => void;
    updateView: (fn: (view: AstView) => AstView, options?: Partial<SetViewOptions>) => void;
    view: AstView;

    setSelectedNodeIds: (nodes: string[]) => void;
    updateSelectedNodes: (fn: (nodes: EnrichedAstNode) => void) => void;
    selectedNodes: EnrichedAstNode[];
    selectedNodeIds: string[];
    selectedNode: OrMixed<EnrichedAstNode> | null;
}

export interface SetViewOptions {
    repairMode: RepairMode;
    disableAutoSave: boolean;
}

const ViewContext = createContext<ViewContextState>({
    setView: () => {},
    updateView: () => {},
    view: {} as AstView,
    setSelectedNodeIds: () => {},
    updateSelectedNodes: () => {},
    selectedNodes: [],
    selectedNodeIds: [],
    selectedNode: null,
});

export class ViewContextProvider extends React.Component<
    {
        children: React.ReactNode;
        view: AstView;
        setView: (view: AstView) => void;
    },
    {
        selectedNodes: EnrichedAstNode[];
        selectedNode: OrMixed<EnrichedAstNode> | null;
    }
> {
    state: {
        selectedNodes: EnrichedAstNode[];
        selectedNode: OrMixed<EnrichedAstNode> | null;
    } = {
        selectedNodes: [],
        selectedNode: null,
    };

    setView = async (view: AstView, options?: Partial<SetViewOptions>) => {
        const finalOptions: SetViewOptions = {
            repairMode: 'change',
            disableAutoSave: false,
            ...options,
        };
        const repairedView = await repair(view, finalOptions.repairMode);
        this.props.setView(repairedView);
    };

    updateView = async (fn: (view: AstView) => AstView, options?: Partial<SetViewOptions>) => {
        const newView = this.props.view;
        if (options?.repairMode === 'lock' || options?.repairMode === 'tryLock') {
            await syncViewToSimulatedV2(newView);
        }
        const result = fn(newView);
        this.setView(result, options);
    };

    setSelectedNodeIds = (nodeIds: string[]) => {
        const nodes = nodeIds.map((id) => findNodeById(this.props.view.tree, id)).filter(Boolean) as EnrichedAstNode[];

        this.setState({
            selectedNodes: nodes,
            selectedNode: deriveSelectedNodeFromSelection(nodes),
        });
    };

    updateSelectedNodes = (fn: (nodes: EnrichedAstNode) => void) => {
        this.state.selectedNodes.forEach(fn);
        this.setView({...this.props.view});
    };

    componentDidUpdate(prevProps: {view: AstView}) {
        if (prevProps.view !== this.props.view) {
            this.setSelectedNodeIds(this.state.selectedNodes.map((n) => n.internalId));
        }
    }

    render() {
        return (
            <ViewContext.Provider
                value={{
                    selectedNode: this.state.selectedNode,
                    selectedNodes: this.state.selectedNodes,
                    selectedNodeIds: this.state.selectedNodes.map((n) => n.internalId),
                    view: this.props.view,
                    updateView: this.updateView,
                    setView: this.setView,
                    setSelectedNodeIds: this.setSelectedNodeIds,
                    updateSelectedNodes: this.updateSelectedNodes,
                }}
            >
                {this.props.children}
            </ViewContext.Provider>
        );
    }
}

export const useViewContext = () => {
    return React.useContext(ViewContext);
};

export function deriveSelectedNodeFromSelection(nodes: EnrichedAstNode[]): OrMixed<EnrichedAstNode> | null {
    if (nodes.length === 0) {
        return null;
    }

    const selectedNode: OrMixed<EnrichedAstNode> = _.cloneDeep(nodes[0]);

    if (nodes.length === 1) {
        return selectedNode;
    }

    for (const node of nodes) {
        updateKeysToMixed(selectedNode, node, ['children']);
    }

    return selectedNode;
}

function updateKeysToMixed<T extends Object>(workingNode: T, comparingNode: T, alwaysMixed: string[]) {
    Object.keys(workingNode).forEach((key) => {
        if (alwaysMixed.includes(key)) {
            workingNode[key] = 'Mixed';
        } else if (comparingNode.hasOwnProperty(key) === false) {
            workingNode[key] = 'Mixed';
        } else if (typeof workingNode[key] === 'object') {
            updateKeysToMixed(workingNode[key], comparingNode[key], alwaysMixed);
        } else {
            if (workingNode[key] !== comparingNode[key]) {
                workingNode[key] = 'Mixed';
            }
        }
    });
}
